import { formatDate } from "@/utils/dateUtils";
import { useMemo } from "react";
import SkeletonMetaRow from "./SkeletonMetaRow";
import SectionHeader from "./SectionHeader";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/Card";

const TenantInfoPanel = ({ tenantData, isLoading }) => {
  const tenantInfoRows = useMemo(() => {
    if (!tenantData) return [];

    const rows = [
      { label: "Tenant Name", value: tenantData?.name ?? "—" },
      { label: "Slug", value: tenantData?.slug ?? "—" },
      { label: "Country", value: tenantData?.region ?? "—" },
      {
        label: "Currency",
        value: tenantData?.currency ? `${tenantData.currencySign} ${tenantData.currency}` : "—",
      },
      { label: "Status", value: tenantData?.isActive ? "Active" : "Inactive" },
      {
        // The seat licence caps invitations, and an AD tenant issues none —
        // every invite route is closed for one, and its sign-in method is fixed
        // at creation, so the stored number never becomes a limit. Shown as
        // what it is rather than as a number nothing enforces.
        label: "Max Users",
        value: tenantData?.adLoginEnabled ? "Does not apply" : (tenantData?.maxUsers ?? "—"),
        note: tenantData?.adLoginEnabled ? "Active Directory creates this tenant's accounts, so no seat licence applies to them." : null,
      },
      {
        label: "Active Directory Sign-in",
        value: tenantData?.adLoginEnabled ? "Enabled" : "Disabled",
      },
      {
        label: "Status Flows",
        value: `${tenantData?.totalCandidateStatusFlows ?? 0} / ${tenantData?.maxCandidateStatusFlows ?? 10}`,
      },
      {
        // The cap on one flow; a job uses its flow's statuses. No tenant-wide
        // total beside it — a sum across flows read as a ratio against this
        // cap, which it is not.
        label: "Max Statuses per Flow",
        value: tenantData?.maxCandidateStatuses ?? 15,
      },
      {
        label: "Database Mode",
        value: tenantData?.dbMode === "dedicated" ? "Dedicated (own database)" : "Shared (RLS on platform DB)",
      },
      {
        label: "Provisioning Status",
        value: tenantData?.provisioningStatus ?? "active",
      },
      {
        label: "Extension Sites",
        value: (tenantData?.enabledExtensionSites ?? []).length ? tenantData.enabledExtensionSites.join(", ") : "None — extension disabled for this tenant",
      },
      { label: "Created At", value: tenantData?.createdAt ? formatDate(tenantData.createdAt) : "—" },
      { label: "Updated At", value: tenantData?.updatedAt ? formatDate(tenantData.updatedAt) : "—" },
    ];

    // Dedicated-tier metadata: DB name, host, role, versions, health.
    // Only shown when the backend actually returned a dbInfo object —
    // legacy responses have this null and we don't render the rows.
    const db = tenantData?.dbInfo;
    if (db) {
      rows.push(
        { label: "DB Name", value: db.dbName ?? db.db_name ?? "—" },
        { label: "DB Host", value: `${db.host ?? "—"}:${db.port ?? "—"}` },
        { label: "DB Role", value: db.roleName ?? db.role_name ?? "—" },
        {
          label: "Alembic Revision",
          value: db.schemaVersion ?? db.schema_version ?? "—",
        },
        {
          label: "RLS Version",
          value: (db.rlsVersion ?? db.rls_version) != null ? `v${db.rlsVersion ?? db.rls_version}` : "—",
        },
        { label: "DB Health", value: db.health ?? "unknown" },
        {
          label: "Last Migration",
          value: (db.lastMigrationAt ?? db.last_migration_at) ? formatDate(db.lastMigrationAt ?? db.last_migration_at) : "—",
        },
      );
    }

    if (tenantData?.deleteReason != null) {
      rows.push({ label: "Delete Reason", value: tenantData.deleteReason ?? "—" });
    }

    return rows;
  }, [tenantData]);

  const renderInfoSkeleton = () => (
    <div className="rounded-b-xl border border-border-primary bg-background-secondary" aria-busy="true">
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <SkeletonMetaRow key={i} lines={1} />
        ))}
      </div>
    </div>
  );

  return (
    <Card className="!p-0 overflow-hidden border-border-primary bg-background-secondary">
      <SectionHeader title="Tenant Information" />

      {isLoading ? (
        renderInfoSkeleton()
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2">
          {tenantInfoRows.map((row, i) => (
            <div
              key={row.label}
              className={cn("flex flex-col gap-1 px-5 py-2 border-b border-border-primary/70", i % 2 === 0 && "md:border-r md:border-border-primary/70")}
            >
              <p className="text-[11px] tracking-wide text-text-muted m-0">{row.label}</p>
              <p className="text-[13px] font-medium text-text-primary m-0 break-words">{row.value}</p>
              {/* A second line inside the same cell, so the two-column grid
                  stays even — a note of its own would offset every row after
                  it. Only "Max Users" carries one today. */}
              {row.note && <p className="text-[11px] text-text-muted m-0">{row.note}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
export default TenantInfoPanel;
